import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatINR } from "../lib/api";
import { Button } from "../components/ui/button";
import { useCart } from "../contexts/CartContext";
import { toast, Toaster } from "sonner";
import { Minus, Plus, ShoppingBag, Leaf, MessageCircle } from "lucide-react";
import { buildWhatsAppLink } from "../components/WhatsAppShareButton";
import { StarRating } from "../components/StarRating";
import ReviewsBlock from "../components/ReviewsBlock";
import SubscribeButton from "../components/SubscribeButton";
import ISOCertificationBadge from "../components/ISOCertificationBadge";
import { useSettings } from "../contexts/SettingsContext";

export default function ProductDetail() {
    const { slug } = useParams();
    const nav = useNavigate();
    const [search] = useSearchParams();
    const { settings } = useSettings();
    const { addItem } = useCart();
    const [product, setProduct] = useState(null);
    const [variant, setVariant] = useState(null);
    const [qty, setQty] = useState(1);
    const [activeImg, setActiveImg] = useState(0);
    const [reviewSummary, setReviewSummary] = useState({ count: 0, average: 0 });

    // Always render size pills ordered cheapest → most expensive so customers
    // anchor on the lowest entry price first. Falls back to the admin-defined
    // order when two variants share the same price.
    const sortedVariants = useMemo(() => {
        if (!product?.variants) return [];
        return [...product.variants].sort((a, b) => (a.price || 0) - (b.price || 0));
    }, [product]);

    useEffect(() => {
        api.get(`/products/${slug}`).then(r => {
            setProduct(r.data);
            // Honour ?size=… deep links from the shop grid so the customer
            // lands directly on the size they previewed. Fall back to the
            // cheapest variant (sorted ascending) when the param is absent.
            const wanted = search.get("size");
            const list = [...(r.data.variants || [])].sort((a, b) => (a.price || 0) - (b.price || 0));
            const fromQuery = wanted
                ? list.find((v) => v.size_label === wanted)
                : null;
            setVariant(fromQuery || list[0] || null);
            setActiveImg(0);
            api.get("/reviews/summary", { params: { target_type: "product", target_id: r.data.id } })
                .then(s => setReviewSummary(s.data))
                .catch(() => { });
        }).catch(() => setProduct(false));
    }, [slug, search]);

    // Reset the gallery thumb index whenever the customer picks a different size —
    // each size can carry its own photo set so a stale index would point nowhere.
    useEffect(() => { setActiveImg(0); }, [variant?.size_id]);

    if (product === false) return <div className="max-w-7xl mx-auto px-6 py-24 text-center"><h1 className="font-serif text-4xl">Product not found</h1></div>;
    if (!product) return <div className="max-w-7xl mx-auto px-6 py-24 text-center text-stone-500">Loading…</div>;

    // Gallery sources: prefer the active variant's per-size photos; fall back to
    // the shared product gallery when this size hasn't been given its own shots.
    const galleryImages = (variant?.images && variant.images.length > 0)
        ? variant.images
        : (product.images || []);

    const onAdd = () => {
        if (!variant) return toast.error("Please choose a size");
        const q = Math.max(1, parseInt(qty, 10) || 1);
        addItem(product, variant, q);
        toast.success(`${product.name} (${variant.size_label}) × ${q} added to cart`);
    };

    const onBuy = () => { onAdd(); nav("/cart"); };

    // ---- Order on WhatsApp ----
    const waNumber = settings?.whatsapp_number || settings?.contact_phone || "";
    const waDigits = String(waNumber).replace(/\D/g, "");
    // Guard against the placeholder "+91 00000 00000" that ships as the default
    // contact phone — tapping it would open an invalid WhatsApp chat.
    const waUsable = waDigits.length >= 10 && !/^0+$/.test(waDigits.slice(-10));
    const waEnabled = settings?.whatsapp_order_enabled !== false && waUsable;
    const waQty = Math.max(1, parseInt(qty, 10) || 1);
    const productUrl = `${window.location.origin}/product/${product.slug || slug}${variant ? `?size=${encodeURIComponent(variant.size_label)}` : ""}`;
    const waMessage = [
        `Hi ${settings?.brand_name || "Hahnemann"}, I'd like to place an order:`,
        ``,
        `Product: ${product.name}`,
        variant ? `Size: ${variant.size_label}` : null,
        `Quantity: ${waQty}`,
        variant ? `Price: ${formatINR(variant.price)} each · ${formatINR(variant.price * waQty)} total` : null,
        ``,
        `Product link: ${productUrl}`,
        ``,
        `Please confirm availability and delivery to my pincode.`,
    ].filter((l) => l !== null).join("\n");
    const waLink = buildWhatsAppLink({ phone: waNumber, message: waMessage });

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="product-detail">
            <Toaster position="top-center" />
            <div className="grid lg:grid-cols-2 gap-12">
                <div className="space-y-3" data-testid="product-gallery">
                    {/* Main image — sourced from the active variant's photos when present, else from the shared gallery */}
                    <div className="bg-stone-100 aspect-square border border-stone-200 overflow-hidden">
                        {galleryImages[activeImg] ? (
                            <img src={galleryImages[activeImg]} alt={product.name} className="w-full h-full object-cover" data-testid="product-gallery-main" />
                        ) : (
                            <div className="w-full h-full grid place-items-center text-stone-400">No image</div>
                        )}
                    </div>
                    {/* Thumbnail strip — only when there are 2+ images */}
                    {galleryImages.length > 1 && (
                        <div className="grid grid-cols-5 gap-2" data-testid="product-gallery-thumbs">
                            {galleryImages.map((src, i) => (
                                <button
                                    key={`${variant?.size_id || "prod"}-${i}`}
                                    onClick={() => setActiveImg(i)}
                                    className={`aspect-square border overflow-hidden transition-all ${i === activeImg ? "border-sky-700 ring-1 ring-sky-700" : "border-stone-200 hover:border-stone-400"}`}
                                    data-testid={`product-thumb-${i}`}
                                    aria-label={`Show image ${i + 1}`}
                                >
                                    <img src={src} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}
                    {/* Tiny pill that confirms what the gallery is showing — only when a variant has its own photos */}
                    {variant?.images && variant.images.length > 0 && (
                        <div className="text-[10px] uppercase tracking-widest text-sky-700 bg-sky-50 border border-sky-200 px-2 py-1 inline-block" data-testid="gallery-source-pill">
                            Showing {variant.size_label} photos
                        </div>
                    )}
                </div>
                <div className="space-y-6">
                    <div className="label-eyebrow text-sky-700">Hahnemann remedy</div>
                    <h1 className="font-serif text-4xl sm:text-5xl text-stone-900 leading-tight">{product.name}</h1>
                    {reviewSummary.count > 0 && (
                        <a href="#reviews" className="inline-block">
                            <StarRating value={reviewSummary.average} count={reviewSummary.count} size={16} showValue dataTestId="product-rating-summary" />
                        </a>
                    )}
                    <p className="text-stone-600 leading-relaxed">{product.short_description}</p>

                    {variant && (
                        <div className="flex items-baseline gap-3">
                            <div className="font-serif text-3xl text-sky-700" data-testid="active-price">{formatINR(variant.price)}</div>
                            <div className="text-xs text-stone-500 uppercase tracking-widest">{variant.size_label}</div>
                            {variant.stock === 0 ? (
                                <span className="text-[10px] uppercase tracking-widest text-red-700 bg-red-50 px-2 py-0.5 border border-red-200" data-testid="active-stock">Out of stock</span>
                            ) : variant.stock <= 5 ? (
                                <span className="text-[10px] uppercase tracking-widest text-amber-700 bg-amber-50 px-2 py-0.5 border border-amber-200" data-testid="active-stock">Only {variant.stock} left</span>
                            ) : (
                                <span className="text-[10px] uppercase tracking-widest text-emerald-700 bg-emerald-50 px-2 py-0.5 border border-emerald-200" data-testid="active-stock">In stock</span>
                            )}
                        </div>
                    )}

                    <div>
                        <div className="flex items-baseline justify-between mb-3">
                            <div className="label-eyebrow text-stone-500">Choose a size · lowest first</div>
                            {sortedVariants.length > 1 && <div className="text-[10px] uppercase tracking-widest text-stone-400">{sortedVariants.length} options</div>}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {sortedVariants.map((v) => {
                                const active = variant?.size_id === v.size_id;
                                const oos = v.stock === 0;
                                return (
                                    <button
                                        key={v.size_id}
                                        onClick={() => !oos && setVariant(v)}
                                        disabled={oos}
                                        className={`text-left px-4 py-3 border transition-all ${
                                            active
                                                ? "border-sky-700 bg-sky-700 text-stone-50"
                                                : oos
                                                    ? "border-stone-200 bg-stone-100 text-stone-400 cursor-not-allowed line-through"
                                                    : "border-stone-300 text-stone-700 hover:border-sky-700 hover:shadow-sm bg-white"
                                        }`}
                                        data-testid={`size-${v.size_label}`}
                                        title={oos ? "Currently out of stock" : ""}
                                    >
                                        <div className="font-mono text-sm font-medium">{v.size_label}</div>
                                        <div className={`text-xs mt-0.5 ${active ? "text-sky-100" : "text-stone-500"}`}>{formatINR(v.price)}</div>
                                        {oos && !active && <div className="text-[9px] uppercase tracking-widest text-red-600 mt-0.5">Out</div>}
                                        {!oos && v.stock <= 5 && !active && <div className="text-[9px] uppercase tracking-widest text-amber-700 mt-0.5">Only {v.stock} left</div>}
                                    </button>
                                );
                            })}
                            {sortedVariants.length === 0 && <span className="text-sm text-stone-500 col-span-3">No sizes available yet</span>}
                        </div>
                    </div>

                    <div className="flex items-center gap-4 flex-wrap">
                        <div className="flex items-center border border-stone-300">
                            <button
                                type="button"
                                onClick={() => setQty((q) => Math.max(1, Number(q || 1) - 1))}
                                className="p-3 hover:bg-stone-50"
                                data-testid="qty-minus"
                                aria-label="Decrease quantity"
                            >
                                <Minus className="w-3.5 h-3.5" />
                            </button>
                            <input
                                type="number"
                                min={1}
                                max={variant?.stock || 999}
                                value={qty}
                                onChange={(e) => {
                                    const n = parseInt(e.target.value, 10);
                                    setQty(Number.isFinite(n) && n > 0 ? n : "");
                                }}
                                onBlur={() => { if (!qty || qty < 1) setQty(1); }}
                                className="w-14 text-center text-sm font-medium bg-white outline-none border-x border-stone-200 py-3"
                                data-testid="qty-value"
                                aria-label="Quantity"
                            />
                            <button
                                type="button"
                                onClick={() => setQty((q) => Number(q || 1) + 1)}
                                className="p-3 hover:bg-stone-50"
                                data-testid="qty-plus"
                                aria-label="Increase quantity"
                            >
                                <Plus className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        <Button onClick={onAdd} disabled={!variant || variant.stock === 0} variant="outline" className="rounded-none border-stone-400 px-6 py-6" data-testid="add-to-cart">
                            <ShoppingBag className="w-4 h-4 mr-2" />Add to cart
                        </Button>
                        <Button onClick={onBuy} disabled={!variant || variant.stock === 0} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50 px-6 py-6" data-testid="buy-now">Buy now</Button>
                        <SubscribeButton product={product} variant={variant} />
                    </div>

                    {waEnabled && (
                        <a
                            href={waLink}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-3 flex items-center justify-center gap-2 w-full border border-emerald-600 text-emerald-700 hover:bg-emerald-50 px-6 py-4 text-sm font-medium transition-colors"
                            data-testid="whatsapp-order-btn"
                        >
                            <MessageCircle className="w-4 h-4" />
                            Order on WhatsApp
                            <span className="text-[10px] uppercase tracking-widest text-emerald-600/80 ml-1">
                                {variant ? `${variant.size_label} × ${waQty}` : "chat with us"}
                            </span>
                        </a>
                    )}

                    {/* ISO / GMP trust badge — sits right under the CTA so it
                        reinforces buyer confidence at the moment of decision. */}
                    <ISOCertificationBadge settings={settings} variant="block" className="mt-2" />

                    <div className="border-t border-stone-200 pt-6 space-y-4 text-sm">
                        {product.description && (<div><div className="label-eyebrow text-stone-500 mb-2">Description</div><p className="text-stone-700 leading-relaxed whitespace-pre-line">{product.description}</p></div>)}
                        {product.indications && (<div><div className="label-eyebrow text-stone-500 mb-2">Indications</div><p className="text-stone-700 leading-relaxed">{product.indications}</p></div>)}
                        {product.composition && (<div><div className="label-eyebrow text-stone-500 mb-2">Composition</div><p className="text-stone-700 leading-relaxed">{product.composition}</p></div>)}
                        <div className="flex items-center gap-2 text-xs text-stone-500 pt-2"><Leaf className="w-3.5 h-3.5 text-sky-700" /> Manufactured in Hahnemann GMP-certified lab.</div>
                    </div>
                </div>
            </div>

            <div id="reviews">
                <ReviewsBlock targetType="product" targetId={product.id} label="Product reviews" />
            </div>
        </div>
    );
}
